import {
  getChannelFunding,
  getPIXmlns,
  setChapterLink,
  setFundingTags,
  setPIXmlns,
  setSoundbites,
  setTranscriptLinks,
} from "./rssFunctions";
import { Element, js2xml, xml2js } from "xml-js";

import {
  getChannelDescription,
  getChannelLocked,
  getChannelLockedOwner,
  getChannelTitle,
  getImgUrl,
  getItems,
  upsertLockedTag,
} from "./rssLenses";
import { Elm } from "./src/Main";

type ExtractElmPortData<
  TProp extends keyof Elm.Main.App["ports"]
> = Elm.Main.App["ports"][TProp] extends {
  subscribe(callback: (data: infer TData) => void): void;
}
  ? TData
  : Elm.Main.App["ports"][TProp] extends {
      send(data: infer TData): void;
    }
  ? TData
  : unknown;

export type Funding = ExtractElmPortData<"sendFunding">;

export type ChaptersLink = ExtractElmPortData<"sendChaptersLink">;

export type Channel = ExtractElmPortData<"rssChannelUpdatePort">;

export type ItemsArray = Channel["items"];
export type RssItem = ItemsArray[0];

export type TranscriptLinks = ExtractElmPortData<
  "sendTranscriptLinks"
>["links"];
export type TranscriptLink = TranscriptLinks[0];

export type Soundbite = ExtractElmPortData<"sendSoundbites">["soundbites"][0];

interface IModel {
  rssFeedContent: string;
}

const tsModel: IModel = {
  rssFeedContent: "",
};
let app: Elm.Main.App;
document.addEventListener("DOMContentLoaded", function () {
  app = Elm.Main.init({
    flags: null,
  });

  app.ports.setFeed.subscribe(async (feedContent: string) => {
    tsModel.rssFeedContent = feedContent;
    const channel = parseFeed(feedContent);
    // console.log('Sendingn ', channel)
    app.ports.rssChannelUpdatePort.send(channel);
  });

  app.ports.sendLocked.subscribe(async ({ locked, owner }) => {
    const updFunction = (feed: Element) => updateLocked(feed, locked, owner);
    const channel = parseAndUpdateFeed(updFunction);
    // console.log("# Send channel", channel);
    app.ports.rssChannelUpdatePort.send(channel);
  });
  app.ports.sendPiXmlns.subscribe(async (piXmlns: string) => {
    const updFunction = (feed: Element) => updatePiXmlns(piXmlns, feed);
    const channel = parseAndUpdateFeed(updFunction);
    // console.log("# Send channel", channel);
    app.ports.rssChannelUpdatePort.send(channel);
  });

  app.ports.sendFunding.subscribe(async (fundingArr: Funding) => {
    const updFunction = (feed: Element) => setFundingTags(fundingArr, feed);
    const channel = parseAndUpdateFeed(updFunction);
    // console.log("Got funding ", fundingArr);
    app.ports.rssChannelUpdatePort.send(channel);
  });

  app.ports.sendChaptersLink.subscribe(async (chaptersLink: ChaptersLink) => {
    const updFunction = setChapterLink(
      chaptersLink.url,
      chaptersLink.mime
    )(chaptersLink.guid);
    const channel = parseAndUpdateFeed(updFunction);
    // console.log("Got chaptersLink ", chaptersLink);
    app.ports.rssChannelUpdatePort.send(channel);
  });

  app.ports.sendTranscriptLinks.subscribe(async ({ guid, links }) => {
    const updFunction = setTranscriptLinks(links)(guid);
    const channel = parseAndUpdateFeed(updFunction);
    app.ports.rssChannelUpdatePort.send(channel);
  });

  app.ports.sendSoundbites.subscribe(async ({ guid, soundbites }) => {
    console.log("### soundbites", guid, soundbites);
    const updFunction = setSoundbites(soundbites)(guid)
    const channel = parseAndUpdateFeed(updFunction);
    app.ports.rssChannelUpdatePort.send(channel);
  });
});

export const parseAndUpdateFeed = (
  actionFunction: (feed: Element) => Element
) => {
  const feed: Element = xml2js(tsModel.rssFeedContent, {
    compact: false,
  }) as Element;

  // console.log("Parsed feed", JSON.stringify(feed));
  const updatedFeed: Element = actionFunction(feed);
  // console.log("Updated feed", updatedFeed);
  tsModel.rssFeedContent = js2xml(updatedFeed, {
    attributeValueFn: function (value) {
      return value.replace(/&/g, "&amp;");
    },
  });
  const channel = parseFeed(tsModel.rssFeedContent);
  // console.log("# Send channel", channel);
  return channel;
  app.ports.rssChannelUpdatePort.send(channel);
};

export const updatePiXmlns = (piXmlns: string, feed: Element) =>
  setPIXmlns(piXmlns, feed);

export const updateLocked = (
  feed: Element,
  locked: boolean,
  owner?: string
) => {
  return upsertLockedTag(locked, owner || "", feed);
};

export const parseFeed = (feedContent: string) => {
  // console.log("### PARSE FEED");
  try {
    const rssFeed: any = xml2js(feedContent, { compact: false });

    const parsed = rssFeed;
    // console.log("## PARSED LOCKED", getChannelLocked(parsed));
    const channel: Channel = {
      piFeedXmlns: getPIXmlns(parsed),
      description: getChannelDescription(parsed),
      imageUrl: getImgUrl(parsed),
      items: getItems(parsed),
      title: getChannelTitle(parsed),
      locked: getChannelLocked(parsed) === "yes",
      lockedOwner: getChannelLockedOwner(parsed),
      feedContent,
      funding: getChannelFunding(parsed),
    };
    return channel;
  } catch (e) {
    console.error("Error happened", e);
    throw new Error("Failed to parse feed");
  }
};

export const formatItem = (item: any) => ({
  title: item.title._text || "",
  guid: item.guid._text || "",
  imageUrl: item["itunes:image"]._attributes.href || "",
  chapterLink: null,
});
